import { delay, takeEvery, eventChannel } from 'redux-saga';
import { put, fork, take, call, select } from 'redux-saga/effects';
import * as SagaKit from 'redux-saga-kit';
import { actions, types } from '../actions/SessionActions';
import { actions as settingsActions } from '../actions/SettingsActions';
import { actions as fedopsActions } from '@wix/restaurants-redux/dist/actions/FedopsActions';
import { actions as formActions, types as formTypes } from '../actions/FormActions';
import * as WixRestBI from '@wix/wixrest-bi';
import TimeUtils from '@wix/wixrest-utils/dist/Time';
import { types as organizationTypes } from '@wix/restaurants-redux/dist/actions/OrganizationActions';
import { actions as historyManagerActions, types as historyManagerTypes } from '@wix/restaurants-redux/dist/actions/HistoryManagerActions';
import { STATE_IDS } from '../shared/constants';
import { createReservationRequestObj } from '../shared/utils/ReservationHelpers';
import _ from 'lodash';
import {PolicyHelper} from "../shared/utils/policyHelper";
import { PropertiesUtils as OPU } from '@wix/wixrest-utils';
import Properties from '@wix/wixrest-properties';

let boundingRect = null;

// ============================================================================
// SAGAS
// ============================================================================

const initSession = ({Utils}) => function* () {
    yield put(historyManagerActions.initHistory({useBrowserHistory: !Utils.isSafari(), initialState: {stateId: STATE_IDS.START}}));
};

const closeDetailsForm  = ({Wix}) => function* () {
    if (boundingRect && Wix.Utils.getDeviceType() === 'mobile') {
        Wix.scrollTo(boundingRect.offsets.x, Math.max(boundingRect.offsets.y - 100, 0));
    }
    yield put(actions.formEnd());
};

const stateUpdate = ({Wix}) => function* ({payload}) {
    const { state } = payload;

    switch (state.stateId) {
        case STATE_IDS.START:
            yield put(actions.gotoStart());
            break;
        case STATE_IDS.FORM:
            yield put(actions.showDetailsForm());
            yield call(scrollToTop({Wix}));
            break;
        case STATE_IDS.SUCCESS:
            yield put(actions.reservationSuccess());
            yield put(historyManagerActions.resetHistory());
            yield put(actions.initSession());
            break;
        case STATE_IDS.ERROR:
            yield put(actions.reservationError());
            break;
        case STATE_IDS.SMS:
            yield put(actions.showSmsValidation());
            break;
        case STATE_IDS.NOT_ENABLED_ERROR:
            yield put(actions.showNotEnabled());
            break;
        case STATE_IDS.PREVIEW_NOT_ENABLED_ERROR:
            yield put(actions.showNotEnabled({preview: true}));
            break;
        case STATE_IDS.PREVIEW_FORM:
            yield put(actions.showDetailsForm({preview: true}));
            break;
        case STATE_IDS.PREVIEW_SUCCESS:
            yield put(actions.reservationSuccess({preview: true}));
            break;
    }
};

const fullOrganizationLoad = (() => {
    let hasLoaded = false;
    return ({ReservationUtils, Wix}) => function* ({payload}) {
        const { organization } = payload;
        const isEnabled = yield call(getIsEnabled({ReservationUtils}), organization);
        yield put(actions.setActivationStatus({isEnabled}));

        if (!hasLoaded) {
            hasLoaded = true;
            Wix.Performance.applicationLoadingStep(2, 'onOrganizationLoad');
        }
    };
})();

const submitReservationForm = ({Wix, WixInstance, moment}) => function* () {
    const isEnabled = yield select(isActiveReservationSelector);
    const organization = yield select(restaurantSelector);
    const isPremiumUser = yield call(isPremium, organization);

    const locations = yield select(locationsSelector);
    if (locations && locations.length > 1) {
        const reservationDetails = yield select(reservationSelector);
        // try to get current location
        let currentLocationId = _.get(reservationDetails, 'location.locationId');
        if (!currentLocationId) {
            // if user hasn't change the location (no setLocation action) - take the default one.
            const defaultLocation = locations.find(location => location.isDefault);
            if (typeof defaultLocation.name !== 'string') defaultLocation.name = defaultLocation.name[defaultLocation.locale];
            yield put(formActions.updateField({type: 'location', value: defaultLocation}));
        }
    }

    if (isEnabled && isPremiumUser) {
        yield fork(waitForPattern, {
            patterns: [formTypes.RESERVATION_VALIDATION_OK, formTypes.RESERVATION_VALIDATION_ERROR],
            callback: validateInitialForm({Wix, WixInstance, moment})
        });
        yield put(formActions.validateReservation());
    } else {
        yield call(gotoNotEnabled({Wix}));
    }
};

const submitFullReservationForm = ({restClient, PhoneUtils, Wix, WixInstance, moment}) => function* () {
    const isSendingReservation = yield select(isSendingReservationSelector);

    if (!isSendingReservation) {
        yield put(actions.submitFullReservationFormStart());
        yield fork(waitForPattern, {
            patterns: [formTypes.FULL_RESERVATION_VALIDATION_OK, formTypes.FULL_RESERVATION_VALIDATION_ERROR],
            callback: validateFullForm({restClient, PhoneUtils, Wix, moment, WixInstance})
        });
        yield put(formActions.validateFullReservation());
    }
};

const smsValidation = ({WixInstance, restClient}) => function* ({payload}) {
    const { code } = payload;

    if (code) {
        yield fork(sendSmsValidation({WixInstance, restClient}), {code});
    } else {}
};

const goBack = () => function* () {
    yield put(historyManagerActions.goBack());
};

const previewReservations = () => function* () {
    yield put(actions.gotoStart());
    yield delay(500);
    yield put(historyManagerActions.pushState({state: {stateId: STATE_IDS.PREVIEW_FORM}}));
};

const upgrade = ({window, Premium}) => function* () {
    window.open(Premium.getPremiumUrl({referral:'restaurants_reservations_preview'}), '_blank');
};

const appLoaded = ({Wix, window, $}) => function* () {
    Wix.Performance.applicationLoaded();

    yield put(settingsActions.windowSize({width: $(window).outerWidth(), height: $(window).outerHeight()}));

    if (Wix.Utils.getViewMode() === 'editor') {
        yield call(addResizeEventListener({Wix, window, $}));
    }
};

const scrollToTop = ({Wix}) => function* () {
    const isIpad = _.get(window, 'navigator.userAgent', '').match(/iPad/i) !== null;

    if (Wix.Utils.getDeviceType() === 'mobile' || isIpad) {
        Wix.getBoundingRectAndOffsets(res => {
            boundingRect = res;
        });

        Wix.scrollTo(0, 0);
    }
};

const openDashboard = (() => {
    let dashboardWindow = null;

    return ({WixInstance, window}) => function*() {
        if (!dashboardWindow || dashboardWindow.closed) {
            let url = 'http://www.wix.com/my-account/sites/';
            try {
                const appDefId = WixInstance.getAppId();
                const metasiteId = WixInstance.getMetasiteIdCalculate();
                url += `${metasiteId}/app/${appDefId}?appState=${encodeURIComponent('?view=reservations')}`;
            } catch (e) {}
            dashboardWindow = window.open(url, '_blank');
        }
        if (dashboardWindow && dashboardWindow.focus) {
            dashboardWindow.focus();
        }
    };
})();

// ============================================================================
// HELPERS
// ============================================================================

function* waitForPattern ({patterns, callback} = {patterns: [], callback: () => {}}) {
    const result = yield take(patterns);
    yield call(callback, result);
}

const validationOk = ({Wix, WixInstance, moment}) => function* () {
    const reservation = yield select(reservationSelector);
    const restaurants = yield select(restaurantSelector);
    const timezone = _.get(restaurants, 'timezone', '');
    const isPremiumUser = yield call(isPremium, restaurants);
    const activeReservation = yield select(isActiveReservationSelector);
    const selectedDate = moment.tz(reservation.date, timezone).hours(reservation.time.hour).minutes(reservation.time.minutes).seconds(0).milliseconds(0);

    const locationId = _.get(reservation, 'location.locationId');
    const locationName = _.get(reservation, 'location.name');

    yield put(WixRestBI.redux.actions.reservationsReserveClick({
        visitorId: WixInstance.getVisitorId(),
        date: TimeUtils.serializeDate(selectedDate),
        hour: selectedDate.format('HH:mm'),
        time: selectedDate.valueOf(),
        party: reservation.party,
        isMerchant: WixInstance.getPermissions() === 'owner',
        locationGuid: locationId,
        location: locationName
    }));

    if (isPremiumUser && activeReservation) {
        yield put(historyManagerActions.pushState({state: {stateId: STATE_IDS.FORM}}));
    } else {
        yield call(gotoNotEnabled({Wix}));
    }
};

const gotoNotEnabled = ({Wix}) => function* () {
    const viewMode = Wix.Utils.getViewMode();
    const deviceType = Wix.Utils.getDeviceType();
    let stateId = STATE_IDS.NOT_ENABLED_ERROR;
    const activeReservation = yield select(isActiveReservationSelector);
    const organization = yield select(restaurantSelector);
    const isPremiumUser = yield call(isPremium, organization);

    if (activeReservation && viewMode === 'preview' && !isPremiumUser && deviceType !== 'mobile') {
        stateId = STATE_IDS.PREVIEW_NOT_ENABLED_ERROR;
    }

    yield put(historyManagerActions.pushState({state: {stateId}}));
};

function* validationError ({errors}) {
    const mappedErrors = _.map(errors, (error) => {
        return _.indexOf(['date', 'time'], error.type) > -1 && error.error !== 'empty';
    });
    const isUnavailable = _.reduce(mappedErrors, (result, item) => {
        return result && item;
    }, true);

    if (isUnavailable) {
        yield put(historyManagerActions.pushState({state: {stateId: STATE_IDS.FORM}}));
    }
}

const validateInitialForm = ({Wix, WixInstance, moment}) => function* ({type, payload}) {
    switch(type) {
        case formTypes.RESERVATION_VALIDATION_OK:
            yield call(validationOk({Wix, WixInstance, moment}));
            break;
        case formTypes.RESERVATION_VALIDATION_ERROR:
            yield call(validationError, payload);
            break;
    }
};

const validateFullForm = ({restClient, Wix, moment, WixInstance}) => function* ({type, payload}) {
    const reservation = yield select(reservationSelector);
    const locationId = _.get(reservation, 'location.locationId');
    const locationName = _.get(reservation, 'location.name');

    if (type === formTypes.FULL_RESERVATION_VALIDATION_OK) {
        const restaurant = yield select(restaurantSelector);
        const timezone = _.get(restaurant, 'timezone', '');
        const selectedDate = moment.tz(reservation.date, timezone).hours(reservation.time.hour).minutes(reservation.time.minutes).seconds(0).milliseconds(0);

        yield put(WixRestBI.redux.actions.reservationsBookATableClick({
            visitorId: WixInstance.getVisitorId(),
            date: TimeUtils.serializeDate(selectedDate),
            hour: selectedDate.format('HH:mm'),
            time: selectedDate.valueOf(),
            party: reservation.party,
            isMerchant: WixInstance.getPermissions() === 'owner',
            locationGuid: locationId,
            location: locationName
        }));
        yield call(sendStartFullSubmitInteraction);
        yield call(sendReservation({Wix, restClient, WixInstance}));
    } else if (type === formTypes.FULL_RESERVATION_VALIDATION_ERROR) {
        const { errors } = payload;
        yield put(WixRestBI.redux.actions.reservationsFormValidation({
            consentError:(!!errors.showPolicyError).toString(),
            date_error: _.get(_.filter(errors, {type: 'date'})[0], 'error', ''),
            time_error: _.get(_.filter(errors, {type: 'time'})[0], 'error', ''),
            first_name_error: _.get(_.filter(errors, {type: 'firstName'})[0], 'error', ''),
            last_name_error: _.get(_.filter(errors, {type: 'lastName'})[0], 'error', ''),
            phone_number_error: _.get(_.filter(errors, {type: 'phone'})[0], 'error', ''),
            email_error: _.get(_.filter(errors, {type: 'email'})[0], 'error', ''),
            visitorId: WixInstance.getVisitorId(),
            locationGuid: locationId
        }));
    }
    yield put(actions.submitFullReservationFormEnd());
};

const sendReservation = ({Wix, restClient, WixInstance}) => function* () {
    const reservationDetails = yield select(reservationSelector);
    const restaurant = yield select(restaurantSelector);
    const reservation = createReservationRequestObj(reservationDetails, restaurant);
    const isPreview = yield select(isPreviewSelector);
    const viewMode = Wix.Utils.getViewMode();
    let outcome = 'success';
    const policyBMSettings = PolicyHelper.getPolicyFromOrganization(restaurant);

    const locations = yield select(locationsSelector);
    if (locations && locations.length > 1) {
        // ML - add locationId to the request
        reservation.locationId = _.get(reservationDetails, 'location.locationId');
    }

    try {
        let stateId = STATE_IDS.SUCCESS;
        const path = `/organizations/${restaurant.id}/reservations`;
        const res = yield call(restClient, path, 'post', reservation);
        yield put(actions.reservationResponse({reserveResponse: res}));

        if (res.status === 'pending') {
            stateId = STATE_IDS.SMS;
            outcome = 'sms';
        } else if (isPreview && viewMode === 'preview') {
            stateId = STATE_IDS.PREVIEW_SUCCESS;
        }

        yield put(historyManagerActions.pushState({state: {stateId}}));
    } catch (e) {
        outcome = 'error';
        yield put(actions.reservationResponse({errorResponse: e}));
        yield put(historyManagerActions.pushState({state: {stateId: STATE_IDS.ERROR}}));
    }

    yield call(sendStartFullSubmitInteractionEnd);

    const partySize = reservation.partySize;
    const specialRequests = reservation.comment || null;
    let customFields = OPU.get(reservation, Properties.Reservations.CUSTOMFIELDS, 'com.wix.restaurants') || null;
    customFields = customFields && customFields.map(cf => `${cf.label}:::${cf.value}`).join('@@@');

    const locationId = _.get(reservationDetails, 'location.locationId');
    const locationName = _.get(reservationDetails, 'location.name');

    yield put(WixRestBI.redux.actions.reservationsOutcome({
        visitorId: WixInstance.getVisitorId(),
        isConsent:(!!policyBMSettings).toString(),
        isCheckBox: policyBMSettings && !!policyBMSettings.policyCheckbox,
        outcome,
        partySize,
        specialRequests,
        customFields,
        isMerchant: WixInstance.getPermissions() === 'owner',
        locationGuid: locationId,
        location: locationName
    }));
};

const sendSmsValidation = ({WixInstance, restClient}) => function* ({code}) {
    const reservationResponse = yield select(reservationResponseSelector);
    const restaurant = yield select(restaurantSelector);
    let res = null;
    let outcome = 'sms';
    let policyBMSettings = PolicyHelper.getPolicyFromOrganization(restaurant);

    if (reservationResponse) {
        try {
            const path = `/organizations/${restaurant.id}/reservations/${reservationResponse.id}/confirm?as=customer&token=${code}`;
            res = yield call(restClient, path, 'post');
            outcome = 'success';
            yield put(actions.reservationResponse({reserveResponse: res, errorResponse: {}}));
            yield put(historyManagerActions.pushState({state: {stateId: STATE_IDS.SUCCESS}}));
        } catch (e) {
            yield put(actions.reservationResponse({errorResponse: e}));
        }
    }

    const reservationDetails = yield select(reservationSelector);
    const locationId = _.get(reservationDetails, 'location.locationId');
    const locationName = _.get(reservationDetails, 'location.name');

    yield put(WixRestBI.redux.actions.reservationsOutcome({
        visitorId: WixInstance.getVisitorId(),
        outcome,
        isConsent:(!!policyBMSettings).toString(),
        isCheckBox: policyBMSettings && !!policyBMSettings.policyCheckbox,
        isMerchant: WixInstance.getPermissions() === 'owner',
        locationGuid: locationId,
        location: locationName
    }));
};

const getIsEnabled = ({ReservationUtils}) => function* (organization) {
    return ReservationUtils.isEnabled(organization).isEnabled;
};

function isPremium(organization) {
    const ids = ['com.wix.restaurants.reservations', 'com.wix.restaurants.orders'];
    return _.some((organization || {}).products || {}, product => {
        return _.some(ids, id => id === product.id) && product.param === 'premium';
    });
}

const addResizeEventListener = ({Wix, window, $}) => function* () {
    let isListening = true;
    let resizeChannel = yield call(windowResizeChannel, {window, $});
    yield fork(takeEveryWindowResize, resizeChannel);
    const viewModeChannel = yield call(viewModeChangeChannel, {Wix});

    yield* takeEvery(viewModeChannel, function* ({viewMode}) {
        if (viewMode !== 'editor' && isListening) {
            isListening = false;
            resizeChannel.close();
        } else if (viewMode === 'editor' && !isListening) {
            isListening = true;
            resizeChannel = yield call(windowResizeChannel, {window, $});
            yield fork(takeEveryWindowResize, resizeChannel);
        }
    });
}

function* takeEveryWindowResize (chan) {
    yield* takeEvery(chan, windowResizeDispatcher);
}

function* windowResizeDispatcher () {
    yield put(settingsActions.windowSize({width: $(window).outerWidth(), height: $(window).outerHeight()}));
}

function windowResizeChannel ({window, $}) {
    return eventChannel(emitter => {
        $(window).on('resize', onResize);

        return () => {
            $(window).off('resize', onResize);
        }

        function onResize () {
            emitter('resize');
        }
    });
}

function viewModeChangeChannel ({Wix}) {
    return eventChannel(emitter => {
        Wix.addEventListener(Wix.Events.EDIT_MODE_CHANGE, (res) => {
            if (res) {
                emitter(res);
            }
        })

        return () => {
            Wix.removeEventListener(Wix.Events.EDIT_MODE_CHANGE)
        };
    });
}

const sendStartFullSubmitInteraction = function* () {
    yield put(fedopsActions.interactionStart({name: 'full-reservation-submit'}));
};

const sendStartFullSubmitInteractionEnd = function* () {
    yield put(fedopsActions.interactionEnd({name: 'full-reservation-submit'}));
};

// ============================================================================
// SELECTORS
// ============================================================================

const reservationSelector = SagaKit.stateSelector('seatingForm.reservation');
const restaurantSelector = SagaKit.stateSelector('organization.organization', {});
const isActiveReservationSelector = SagaKit.stateSelector('seatingSession.isEnabled');
const isPreviewSelector = SagaKit.stateSelector('seatingSession.preview');
const reservationResponseSelector = SagaKit.stateSelector('seatingSession.reserveResponse');
const isSendingReservationSelector = SagaKit.stateSelector('seatingSession.isSendingReservation', false);
const locationsSelector = SagaKit.stateSelector('locations.locations');

// ============================================================================
// EXPORT
// ============================================================================

export default SagaKit.sagaCreator({
    [types.INIT_SESSION]: {handler: initSession, takeLatest: true},
    [organizationTypes.ORGANIZATION_LOAD]: {handler: fullOrganizationLoad, takeLatest: true},
    [types.CLOSE_DETAILS_FORM]: {handler: closeDetailsForm, takeLatest: true},
    [historyManagerTypes.STATE_UPDATE]: {handler: stateUpdate, takeLatest: true},
    [types.SUBMIT_RESERVATION_FORM]: {handler: submitReservationForm, takeLatest: true, cancelOn: types.SUBMIT_FULL_RESERVATION_FORM},
    [types.SUBMIT_FULL_RESERVATION_FORM]: {handler: submitFullReservationForm, takeLatest: true, cancelOn: types.CLOSE_DETAILS_FORM},
    [types.SMS_VALIDATION]: {handler: smsValidation, takeLatest: true},
    [types.GO_BACK]: {handler: goBack, takeLatest: false},
    [types.PREVIEW_RESERVATIONS]: {handler: previewReservations, takeLatest: true},
    [types.UPGRADE]: {handler: upgrade, takeLatest: true},
    [types.OPEN_DASHBOARD]: {handler: openDashboard, takeLatest: true},
    [types.APP_LOADED]: {handler: appLoaded, takeLatest: true},
    [types.SCROLL_TO_TOP]: {handler: scrollToTop, takeLatest: true}
});
